import { useMemo, useState } from 'react'

import { animationsForProvider } from 'Pods/ThreeDStudio/Shared/Utils/animationsForProvider'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

/**
 * The entries the grid renders: the active engine's supported clips, with the
 * animation the opened run was generated from hoisted to the front, then
 * narrowed by the search term.
 *
 * @param {string} query - Current search term
 * @returns {Object[]} - ANIMATIONS entries in display order
 */
export function useOrderedAnimations(query) {
  const animationId = useThreeDStudioStore(state => state.animateSettings.animationId)
  const enhancedProvider = useThreeDStudioStore(state => state.schema.enhancedProvider)
  const openedModelId = useThreeDStudioStore(state => state.ui.openedModel?.id)
  // Captured when the opened model changes rather than followed live: hoisting
  // whatever is selected would make the grid reshuffle under the pointer as the
  // user clicks through it. setOpenedModel patches ui.openedModel and
  // animateSettings in the same update, so the id read here is already the
  // newly opened run's clip.
  const [pinnedAnimation, setPinnedAnimation] = useState({ openedModelId, animationId })

  if (pinnedAnimation.openedModelId !== openedModelId) {
    setPinnedAnimation({ openedModelId, animationId })
  }

  const pinnedId = pinnedAnimation.animationId

  return useMemo(() => {
    const supported = animationsForProvider(enhancedProvider)
    const ordered = pinnedId != null && supported.some(({ id }) => id === pinnedId)
      ? [
        ...supported.filter(({ id }) => id === pinnedId),
        ...supported.filter(({ id }) => id !== pinnedId),
      ]
      : supported
    const term = query.trim().toLowerCase()

    if (!term) {
      return ordered
    }

    return ordered.filter(({ name }) => name.toLowerCase().includes(term))
  }, [query, enhancedProvider, pinnedId])
}
